import React from 'react';

import {
  BIOENTITY_NAME_COLUMN_NAME,
  ColumnDefinition,
  getColumnDefinitionIndex,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ImportedStructuredTableRow} from 'notebook/structured-tables/spreadsheet-import/prepareImportedRows';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  COLUMN_TYPE_TO_NAME,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';

export interface BulkAddRowsToRegistrationTableFlowProps {
  tagSchemaId: string;
  batchSchemaId: string | null;
  onImportRows: (importedRows: Array<ImportedStructuredTableRow>) => void;
  onRequestClose: () => void;
  columnDefinitions: Array<ColumnDefinition>;
}

class BulkAddRowsToRegistrationTableFlow extends React.Component<BulkAddRowsToRegistrationTableFlowProps> {
  static open(props: Omit<BulkAddRowsToRegistrationTableFlowProps, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkAddRowsToRegistrationTableFlow
        {...props}
        onRequestClose={SpreadsheetBulkImportActions.closeModal}
      />
    );
  }

  _getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({}),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        shouldResolveLinkColumns: false,
        // Needs to be an arrow function so we can access this.props
        // when called from SpreadsheetBulkImportStore._getValidColumnTypes
        getValidColumnTypes: (): Array<COLUMN_TYPE> => [
          COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY,
          COLUMN_TYPE.FILENAME,
          COLUMN_TYPE.STAGE_RUN_NAME,
          ...(getColumnDefinitionIndex({
            columnDefinitionType: TableColumnType.DNA_BASES,
            tableColumnDefinitions: this.props.columnDefinitions,
            isOptional: true,
          }) > -1
            ? [COLUMN_TYPE.RESIDUES]
            : []),
          COLUMN_TYPE.SCHEMA_FIELD,
          COLUMN_TYPE.IGNORE,
        ],
        getRequiredColumnsError(columns: Array<SpreadsheetColumn>): string | null {
          const hasExistingBioentityColumn = columns.some(
            ({type}) => type === COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY
          );
          if (hasExistingBioentityColumn && columns.some(({type}) => type === COLUMN_TYPE.FILENAME)) {
            return `Cannot specify ${BIOENTITY_NAME_COLUMN_NAME} when using an existing entity.`;
          }
          if (hasExistingBioentityColumn && columns.some(({type}) => type === COLUMN_TYPE.SCHEMA_FIELD)) {
            return `Cannot specify ${
              COLUMN_TYPE_TO_NAME[COLUMN_TYPE.SCHEMA_FIELD]
            } when using an existing entity.`;
          }
          return null;
        },
        getValidSchemaFieldIds: () => {
          const schemaFieldIds = [];
          for (const columnDefinition of this.props.columnDefinitions) {
            if (columnDefinition.type === TableColumnType.TAG_VALUE) {
              schemaFieldIds.push(columnDefinition.tagSchemaFieldApiId);
            }
          }
          return schemaFieldIds;
        },
        onValidateSpreadsheet: () => {
          SpreadsheetBulkImportActions.validateNewRowsForRegistrationTableImport();
        },
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: false,
      }),
    },
  ];

  _handleImportFromSpreadsheet = (): void => {
    SpreadsheetBulkImportActions.getNewRowsForRegistrationTableImport(this.props.onImportRows);
  };

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="registration table row"
        importType={IMPORT_TYPE.REGISTRATION_TABLE}
        initialSettings={{
          initialTagSchemaId: this.props.tagSchemaId,
          initialBatchSchemaId: this.props.batchSchemaId,
        }}
        onImportFromSpreadsheet={this._handleImportFromSpreadsheet}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import into registration table"
      />
    );
  }
}

export default BulkAddRowsToRegistrationTableFlow;
